<template>
  <modal-re-init-modal-behaviour>
    <template slot-scope="{ keyId, keyUpdater }">
      <b-modal
        :visible="visible"
        size="xl"
        hide-footer
        header-class="align-items-center"
        @show="keyUpdater"
        @hide="$emit('close')"
        @hidden="clearData"
      >
        <template slot="modal-header">
          <h2 class="v2_font_size_20">Society Email Distribution</h2>
          <div>
            <b-button
              variant="primary"
              :disabled="isButtonDisabled"
              @click="send"
            >
              <template v-if="!sendLoading">Send</template>
              <b-spinner v-else small />
            </b-button>
            <b-button variant="default" @click="$emit('close')">
              Cancel
            </b-button>
          </div>
        </template>

        <v-wait :visible="loading" :for="$options.name">
          <template slot="waiting">
            <HalfCircleSpinner />
            <div class="spinner-message my-3 text-center">
              Getting society data...
            </div>
          </template>
          <template>
            <b-form-group
              label="To:"
              label-cols-sm="3"
              label-align-sm="left"
              class="mb-0 pt-2"
            >
              <b-form-radio-group
                :options="toOptions"
                v-model="to"
                stacked
                :state="validateState('to')"
              />
              <b-form-invalid-feedback :state="validateState('to')">
                {{ invalidFeedback }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
              label="Subject:"
              label-cols-sm="3"
              label-align-sm="left"
              class="mb-0 pt-3"
              :invalid-feedback="invalidFeedback"
            >
              <b-form-input
                v-model="subject"
                placeholder="Subject"
                :state="validateState('subject')"
              />
            </b-form-group>
            <b-form-group
              label="Body:"
              label-cols-sm="3"
              label-align-sm="left"
              class="mb-0 pt-3"
              :invalid-feedback="invalidFeedback"
            >
              <b-form-textarea
                v-model="body"
                placeholder="Body"
                rows="8"
                :state="validateState('body')"
              />
            </b-form-group>
            <b-form-group
              label="Footer:"
              label-cols-sm="3"
              label-align-sm="left"
              class="mb-0 pt-3"
            >
              <b-form-checkbox v-model="isFooter">
                Yes
              </b-form-checkbox>
            </b-form-group>
            <hr/>
            <b-form-group
              label="Test Address:"
              label-cols-sm="3"
              label-align-sm="left"
              class="mb-0 pt-3"
            >
              <b-row>
                <b-col>
                  <b-form-input
                    type="email"
                    v-model="testEmail"
                    :state="validateState('testEmail')"
                  />
                  <b-form-invalid-feedback :state="validateState('testEmail')">
                    {{ testEmailInvalidFeedback }}
                  </b-form-invalid-feedback>
                </b-col>
                <b-col>
                  <b-button
                    variant="primary"
                    :disabled="isButtonDisabled"
                    @click="sendTest"
                  >
                    <template v-if="!sendTestLoading">Send Test</template>
                    <b-spinner v-else small />
                  </b-button>
                </b-col>
              </b-row>
            </b-form-group>
          </template>
        </v-wait>
      </b-modal>
    </template>
  </modal-re-init-modal-behaviour>
</template>

<script>
import ModalReInitModalBehaviour from '@/behaviours/ForceUpdateBehaviour';
import Api from '@/providers/Api';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import { HalfCircleSpinner } from 'epic-spinners';
import { validationMixin } from 'vuelidate';
import { email, required } from 'vuelidate/lib/validators';
import validateStateMixin from '@/mixins/validateStateMixin';

const DISTRIBUTION_TO_TYPES = {
  ALL: 'ALL',
  ADMINS: 'ADMINS',
  REGISTERED: 'REGISTERED',
  UNREGISTERED: 'UNREGISTERED',
  UNCOMPLETED: 'UNCOMPLETED',
  COMPLETED: 'COMPLETED',
};

export default {
  name: 'DistributionEmailBuildingModal',
  mixins: [validationMixin, validateStateMixin],
  components: {
    ModalReInitModalBehaviour,
    HalfCircleSpinner,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    societyId: {
      type: Number,
      required: true,
    },
  },
  data: function () {
    return {
      sendLoading: false,
      sendTestLoading: false,
      loading: false,
      memberData: {},
      to: null,
      subject: null,
      body: null,
      isFooter: false,
      testEmail: null,
    };
  },
  validations: {
    to: {
      required,
    },
    subject: {
      required,
    },
    body: {
      required,
    },
    testEmail: {
      required,
      email,
    },
  },
  created: function () {
    this.getOptionData();
  },
  computed: {
    toOptions: function () {
      return [
        {
          text: `All Society Members – both registered and unregistered (${
            (this.memberData || {})[DISTRIBUTION_TO_TYPES.ALL] || 0
          })`,
          value: DISTRIBUTION_TO_TYPES.ALL,
        },
        // {
        //   text: `All Society admins (${(this.memberData || {})[DISTRIBUTION_TO_TYPES.ADMINS] || 0})`,
        //   value: DISTRIBUTION_TO_TYPES.ADMINS,
        // },
        {
          text: `All Registered Society Members (${
            (this.memberData || {})[DISTRIBUTION_TO_TYPES.REGISTERED] || 0
          })`,
          value: DISTRIBUTION_TO_TYPES.REGISTERED,
        },
        {
          text: `All Unregistered Society Members (${
            (this.memberData || {})[DISTRIBUTION_TO_TYPES.UNREGISTERED] || 0
          })`,
          value: DISTRIBUTION_TO_TYPES.UNREGISTERED,
        },
        {
          text: `All Registered Society Members That Have Not Completed the Primary Active Study (${
            (this.memberData || {})[DISTRIBUTION_TO_TYPES.UNCOMPLETED] || 0
          })`,
          value: DISTRIBUTION_TO_TYPES.UNCOMPLETED,
        },
        {
          text: `All Registered Society Members That Have Completed the Primary Active Study (${
            (this.memberData || {})[DISTRIBUTION_TO_TYPES.COMPLETED] || 0
          })`,
          value: DISTRIBUTION_TO_TYPES.COMPLETED,
        },
      ];
    },
    isButtonDisabled: function () {
      return !!this.sendLoading || !!this.loading || !!this.sendTestLoading;
    },
    invalidFeedback: function () {
      return 'This field is required';
    },
    testEmailInvalidFeedback: function () {
      return !this.$v.testEmail.email
        ? 'Test Address Email should be a valid email.'
        : 'This field is required';
    },
  },
  methods: {
    getOptionData: async function () {
      this.loading = true;
      try {
        this.memberData = await Api.getDistributionOptionData(this.societyId);
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.loading = false;
      }
    },
    send: async function () {
      this.$v.to.$touch();
      this.$v.subject.$touch();
      this.$v.body.$touch();
      this.$v.testEmail.$reset();
      if (this.$v.$anyError) {
        return;
      }

      this.sendLoading = true;
      try {
        const data = {
          societyId: this.societyId,
          to: this.to,
          subject: this.subject,
          body: this.body,
          isFooter: this.isFooter,
        };
        await Api.sendDistributionEmail(data);
        this.$toasted.success('Distribution Email sent successfully.', {
          duration: 3000,
        });
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.sendLoading = false;
        this.$v.$reset();
      }
    },
    sendTest: async function () {
      this.$v.to.$reset();
      this.$v.testEmail.$touch();
      this.$v.subject.$touch();
      this.$v.body.$touch();
      if (this.$v.$anyError) {
        return;
      }

      this.sendTestLoading = true;
      try {
        const data = {
          societyId: this.societyId,
          testEmail: this.testEmail,
          subject: this.subject,
          body: this.body,
          isFooter: this.isFooter,
        };
        await Api.sendDistributionTestEmail(data);
        this.$toasted.success('Distribution Test Email sent successfully.', {
          duration: 3000,
        });
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.sendTestLoading = false;
        this.$v.$reset();
      }
    },
    clearData: function () {
      this.to = null;
      this.subject = null;
      this.body = null;
      this.testEmail = null;
      this.isFooter = false;
      this.$v.$reset();
    }
  },
};
</script>

<style scoped/>
